import logger from '@finn-no/fiaas-logger';
import type { NextPage } from 'next';
import { fetchVariants } from 'shared/experiment/variants';
import { getPageCounter } from 'shared/prometheus/prometheus';
import type { SspContext } from 'shared/types';
import { fetchOffers } from '@/api/fetcher';
import { ResultPageLayout } from '@/components/ResultPageLayout';
import { config, feConfig } from '@/config';
import { ExperimentProvider } from '@/hooks/experiments';
import { browserQueryParamsToFilters, filtersToApiQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { runAllMiddlewares } from '@/server/middleware';
import { actions as filterActions } from '@/state/filterActions';
import { actions as resultActions } from '@/state/resultActions';
import { wrapper } from '@/state/store';

type ResultPageProps = {
    hasLegalBasis: boolean;
    unleash: Record<string, boolean> | null;
    config: typeof feConfig;
    experiments: { [key: string]: string };
};

const ResultPage: NextPage<ResultPageProps> = (props: ResultPageProps) => {
    wrapper.useHydration(props);

    return (
        <ExperimentProvider experiments={props.experiments}>
            <ResultPageLayout />
        </ExperimentProvider>
    );
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async ({ req, res, query }: SspContext) => {
    if (query.skipSSRData) {
        return { props: { hasLegalBasis: false, unleash: null, config: feConfig, experiments: {} } };
    }

    await runAllMiddlewares(req, res, query);

    const experiments =
        (await fetchVariants(req, res, 'travel-pale-pp-web', config.get('experimentsApiUrl'), [
            'travel-prepackage-clickable_result_item',
        ])) ?? {};

    const filters = browserQueryParamsToFilters(query);
    store.dispatch(filterActions.filter(filters));

    try {
        const data = await fetchOffers(config.get('pp.apiUrlInternal'), filtersToApiQueryParams(filters));
        store.dispatch(resultActions.setResults(data));
    } catch (error) {
        store.dispatch(resultActions.setError());
        logger.error(`Failed to fetch offers for result page: ${error}`);
    }

    getPageCounter().inc({ app: 'pale-pp-web', page: 'result' });

    return {
        props: {
            hasLegalBasis: res.locals?.hasLegalBasis ?? false,
            unleash: res.locals?.unleash ?? null,
            config: feConfig,
            experiments: experiments,
        },
    };
});

export default ResultPage;
